import {
  Button,
  Flex,
  Heading,
  IconAddLine,
  IconArrowStartLine,
  Spinner,
  TextInput,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Link, useNavigate } from "react-router";
import { Files } from "../../services/FilesService";
import { ReflectionTemplateService } from "../../services/ReflectionTemplateService";
import { UserDetailContext } from "../../utils/Context";
import { fileWriter } from "../../utils/FileUtils";
import { sort } from "../../utils/utils";
import generateUuid from "../../utils/Uuid";
import { Card } from "../Card";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { fetchHelperTextForTemplate } from "../Reflection/utils";
import { MULTI, Selector, SINGLE } from "../Selectors/Selector";
import AcademicYearSelect from "../TermManagement/AcademicYears/AcademicYearSelect";
import TemplateQuestionCard from "./TemplateQuestionCard";

const ManageReflectionTemplate = ({
  mode,
  templates,
  template,
  setTemplates,
  cycleTypes,
  allDepartments,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const navigate = useNavigate();
  const [templateName, setTemplateName] = useState("");
  const [selectedCycleType, setSelectedCycleType] = useState({});
  const [startYear, setStartYear] = useState({});
  const [endYear, setEndYear] = useState({});
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const filteredDepartments = allDepartments;

  function templateVerification() {
    let valid = true;
    questions.forEach((question) => {
      if (question.header_question.trim() === "") {
        showNotification(t("reflectionTemplates.questionNotEmpty"), ERROR);
        valid = false;
      }
    });
    if (startYear.start_at >= endYear.end_at) {
      showNotification(t("reflectionTemplates.invalidDatesError"), ERROR);
      valid = false;
    }
    return valid;
  }

  useEffect(() => {
    async function fetchHelperText() {
      try {
        const helperTexts = await fetchHelperTextForTemplate(template.sections);
        setQuestions(
          template.sections.map((question, i) => ({
            ...question,
            helper_text: helperTexts[i].content || "",
          }))
        );
        setLoadingQuestions(false);
      } catch {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("reflectionTemplates.additionalInstructions"),
          }),
          ERROR
        );
      }
    }
    if (template) {
      setTemplateName(template.name);
      setSelectedCycleType(
        cycleTypes.find((c) => c.id === template.cycle_type_id) || {}
      );
      setStartYear(template.start_year);
      setEndYear(template.end_year);
      setSelectedDepartments(template.departments.map((d) => d.id));
      fetchHelperText();
    }
  }, [template, showNotification, cycleTypes]);

  async function saveHelperTexts(questions) {
    try {
      const promiseList = questions.map((question) => {
        const helperText = fileWriter(
          question.helper_text,
          "response.html",
          "text/html"
        );
        return Files.saveFile(helperText, "templateHelperText", question.id);
      });
      await Promise.all(promiseList);
    } catch {
      throw new Error("Error saving helper texts");
    }
  }

  function verifyDateOverlap(
    selectedStartDate,
    selectedEndDate,
    existingTemplate
  ) {
    const existingStartDate = new Date(existingTemplate.start_year.end_at);
    const existingEndDate = new Date(existingTemplate.end_year.start_at);

    return (
      selectedStartDate <= existingEndDate &&
      existingStartDate <= selectedEndDate
    );
  }

  function verifyOverlap(start, end, departments) {
    let valid = true;
    for (const existingTemplate of templates) {
      if (mode === "edit" && existingTemplate.id === template.id) {
        continue;
      }
      const departmentOverlap = existingTemplate.departments.some((dept) =>
        departments.includes(dept.id)
      );
      if (departmentOverlap) {
        const startDate = new Date(start.start_at);
        const endDate = new Date(end.end_at);
        if (verifyDateOverlap(startDate, endDate, existingTemplate)) {
          showNotification(
            t("reflectionTemplates.conflictingTemplateError", {
              template: existingTemplate.name,
            }),
            ERROR
          );
          valid = false;
          break;
        }
      }
    }
    return valid;
  }

  async function handleCreateTemplate() {
    try {
      const validTemplate =
        templateVerification() &&
        verifyOverlap(startYear, endYear, selectedDepartments);
      if (validTemplate) {
        setSubmitting(true);
        const newTemplate = await ReflectionTemplateService.createTemplate({
          template: {
            author: userDetails.name,
            name: templateName,
            cycle_type_id: selectedCycleType.id,
            sections: questions.map((question) => ({
              header_question: question.header_question,
            })),
          },
          departments: selectedDepartments,
          startDate: startYear.id,
          endDate: endYear.id,
        });
        newTemplate.departments = allDepartments.filter((d) =>
          selectedDepartments.includes(d.id)
        );
        newTemplate.start_year = startYear;
        newTemplate.end_year = endYear;
        await saveHelperTexts(
          newTemplate.sections.map((question, i) => ({
            ...question,
            id: newTemplate.sections[i].id,
            helper_text: questions[i].helper_text,
          }))
        );
        setTemplates((prevTemplates) =>
          sort([...prevTemplates, newTemplate], (t) => t.name)
        );
        navigate("/setup/reflection-templates/");
        showNotification(
          t("notifications.general.createSuccess", {
            obj: t("reflectionTemplates.reflectionTemplate"),
          })
        );
      }
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflectionTemplates.reflectionTemplate"),
        }),
        ERROR
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdateTemplate() {
    try {
      const validTemplate =
        templateVerification() &&
        verifyOverlap(startYear, endYear, selectedDepartments);
      if (validTemplate) {
        setSubmitting(true);
        const updatedTemplate = await ReflectionTemplateService.updateTemplate({
          id: template.id,
          template: {
            author: userDetails.name,
            name: templateName,
            cycle_type_id: selectedCycleType.id,
            sections: questions.map((question) => ({
              header_question: question.header_question,
              id: question.id,
            })),
          },
          departments: selectedDepartments,
          startDate: startYear.id,
          endDate: endYear.id,
          deleteSections: template.sections
            .filter((s) => !questions.some((q) => q.id === s.id))
            .map((s) => s.id),
        });
        updatedTemplate.departments = allDepartments.filter((d) =>
          selectedDepartments.includes(d.id)
        );
        updatedTemplate.start_year = startYear;
        updatedTemplate.end_year = endYear;
        await saveHelperTexts(
          updatedTemplate.sections.map((question, i) => ({
            ...question,
            id: updatedTemplate.sections[i].id,
            helper_text: questions[i].helper_text,
          }))
        );
        setTemplates((prevTemplates) =>
          prevTemplates.map((t) =>
            t.id === updatedTemplate.id ? updatedTemplate : t
          )
        );
        navigate("/setup/reflection-templates/");
        showNotification(
          t("notifications.general.updateSuccess", {
            obj: t("reflectionTemplates.reflectionTemplate"),
          })
        );
      }
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("reflectionTemplates.reflectionTemplate"),
        }),
        ERROR
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleCycleTypeSelect(typeId) {
    setSelectedCycleType(cycleTypes.find((c) => c.id === typeId));
  }

  function handleAddQuestion() {
    setQuestions((prevQuestions) => [
      ...prevQuestions,
      { key: generateUuid(), header_question: "", helper_text: "" },
    ]);
  }

  function handleStartYearSelect(selectedYear) {
    setStartYear(selectedYear[0]);
    if (endYear && selectedDepartments.length > 0) {
      verifyOverlap(selectedYear[0], endYear, selectedDepartments);
    }
  }

  function handleEndYearSelect(selectedYear) {
    setEndYear(selectedYear[0]);
    if (startYear && selectedDepartments.length > 0) {
      verifyOverlap(startYear, selectedYear[0], selectedDepartments);
    }
  }

  function handleDepartmentSelect(selected) {
    setSelectedDepartments(selected);
    if (startYear && endYear) {
      verifyOverlap(startYear, endYear, selected);
    }
  }

  function enableCreateTemplate() {
    return (
      templateName.trim() &&
      selectedCycleType.id &&
      questions.length > 0 &&
      startYear?.id &&
      endYear?.id &&
      selectedDepartments.length > 0 &&
      !submitting
    );
  }

  return (
    <div
      style={{
        margin: "-0.75rem",
        height: "calc(100vh - 2.375rem - 1.5rem - 3rem - 1.375rem)",
      }}
    >
      <div
        style={{
          margin: "0.75rem 0.75rem 0 0.75rem",
          borderBottom: "1px solid #ccc",
        }}
      >
        <Heading as="h2" level="h3">
          {mode === "edit"
            ? t("templates.editTemplate")
            : t("templates.createTemplate")}
        </Heading>
      </div>
      <Flex direction="column" height="100%">
        {mode === "edit" && (!template || loadingQuestions) ? (
          <Flex.Item padding="small" shouldGrow>
            <Skeleton
              count={3}
              style={{ height: 106, borderRadius: 5, marginBottom: 10 }}
            />
          </Flex.Item>
        ) : (
          <>
            <Flex.Item padding="small">
              <div style={{ display: "flex", gap: "0.75rem" }}>
                <div style={{ width: "50%" }}>
                  <TextInput
                    id="template-name"
                    renderLabel={t("planTemplates.templateName")}
                    value={templateName}
                    onChange={(_e, value) => setTemplateName(value)}
                  />
                </div>
                <div style={{ width: "50%" }}>
                  <Selector
                    id="cycle-type-select"
                    label={t("cyclesAndGoals.typeSelect.label")}
                    selectHandler={handleCycleTypeSelect}
                    options={cycleTypes}
                    selectedItemId={selectedCycleType.id}
                  />
                </div>
              </div>
            </Flex.Item>
            <Flex.Item padding="0 small small small">
              <div style={{ display: "flex", gap: "0.75rem" }}>
                <div style={{ width: "50%" }}>
                  <AcademicYearSelect
                    id="template-start-year"
                    label={t("reflectionTemplates.startYear")}
                    type={SINGLE}
                    selectHandler={handleStartYearSelect}
                    selectedAcademicYears={startYear?.id}
                  />
                </div>
                <div style={{ width: "50%" }}>
                  <AcademicYearSelect
                    id="template-end-year"
                    label={t("reflectionTemplates.endYear")}
                    type={SINGLE}
                    selectHandler={handleEndYearSelect}
                    selectedAcademicYears={endYear?.id}
                  />
                </div>
              </div>
            </Flex.Item>
            <Flex.Item padding="0 small small small">
              <Selector
                id="departments-select"
                type={MULTI}
                label={t("global.departments")}
                options={filteredDepartments}
                selectHandler={handleDepartmentSelect}
                selectedOptions={selectedDepartments}
              />
            </Flex.Item>
            <Flex.Item padding="0 small">
              {questions.map((question, key) => (
                <Card
                  key={question.id || question.key}
                  style={{
                    backgroundColor: "#F1F1F1",
                    border: "1px solid #ccc",
                    height: "auto",
                    margin: "0.75rem 0",
                  }}
                >
                  <TemplateQuestionCard
                    question={question}
                    index={key}
                    lastQuestion={key === questions.length - 1}
                    setQuestions={setQuestions}
                  />
                </Card>
              ))}
            </Flex.Item>
            <Flex.Item padding="small" shouldGrow>
              <Button
                color="primary"
                display="block"
                renderIcon={IconAddLine}
                onClick={handleAddQuestion}
              >
                {t("reflectionTemplates.addQuestion")}
              </Button>
            </Flex.Item>
          </>
        )}
        <Flex.Item padding="0 small">
          <div style={{ borderBottom: "1px solid #ccc" }} />
        </Flex.Item>
        <Flex.Item textAlign="end" padding="0 small small small">
          <Flex textAlign="start" wrap="wrap" gap="small" padding="small 0 0 0">
            <Flex.Item shouldGrow>
              <Link to={{ pathname: "/setup/reflection-templates/" }}>
                <Button renderIcon={IconArrowStartLine}>
                  {t("global.goBack")}
                </Button>
              </Link>
            </Flex.Item>
            <Flex.Item>
              <Button
                color="primary"
                onClick={template ? handleUpdateTemplate : handleCreateTemplate}
                interaction={enableCreateTemplate() ? "enabled" : "disabled"}
              >
                {submitting ? (
                  <Spinner
                    renderTitle={t("global.pleaseWait")}
                    size="x-small"
                  />
                ) : mode === "edit" ? (
                  t("templates.updateTemplate")
                ) : (
                  t("templates.createTemplate")
                )}
              </Button>
            </Flex.Item>
          </Flex>
        </Flex.Item>
      </Flex>
    </div>
  );
};

export default ManageReflectionTemplate;
